// src/utils/formatos.js
// =========================================================================
// Utilidades de formato compartidas por toda la UI.
// Centralizan la lógica que estaba duplicada (con try/catch a mano) en
// decenas de componentes: iniciales, horas, fechas y duraciones.
// =========================================================================
import { format, parseISO, isValid, differenceInMinutes } from 'date-fns';
import { es } from 'date-fns/locale';

/** Iniciales para avatares: ("Canela", "Garrido") -> "CG". */
export const getIniciales = (nombre = '', apellidos = '') => {
    const a = (nombre || '').trim().charAt(0);
    const b = (apellidos || '').trim().charAt(0);
    return `${a}${b}`.toUpperCase() || '?';
};

/** Convierte cualquier entrada (ISO string o Date) en Date válido, o null. */
const aFecha = (valor) => {
    if (!valor) return null;
    const d = valor instanceof Date ? valor : parseISO(String(valor));
    return isValid(d) ? d : null;
};

/** Formatea una fecha de forma segura con un patrón date-fns. Devuelve `fallback` si no es válida. */
export const fechaSegura = (valor, patron = 'dd/MM/yyyy', fallback = '—') => {
    const d = aFecha(valor);
    return d ? format(d, patron, { locale: es }) : fallback;
};

/** Solo la hora: "HH:mm". */
export const formatHora = (valor, fallback = '--:--') => fechaSegura(valor, 'HH:mm', fallback);

/** Solo la fecha: "dd/MM/yyyy". */
export const formatFecha = (valor, fallback = '--/--/----') => fechaSegura(valor, 'dd/MM/yyyy', fallback);

/** Fecha y hora: "dd MMM yyyy · HH:mm". */
export const formatFechaHora = (valor, fallback = '—') => fechaSegura(valor, "dd MMM yyyy · HH:mm", fallback);

/** Minutos -> "Xh Ym". */
export const formatHoras = (mins) => {
    if (!mins || mins < 0) return '0h 0m';
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

/** Duración en minutos entre dos fechas (0 si es negativa o inválida). */
export const minutosEntre = (inicio, fin) => {
    const a = aFecha(inicio);
    const b = aFecha(fin);
    if (!a || !b) return 0;
    const m = differenceInMinutes(b, a);
    return m > 0 ? m : 0;
};

/** Capitaliza cada palabra: "bimba garrido" -> "Bimba Garrido". */
export const capitalizarNombre = (str = '') =>
    str.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

/** Importe en euros con formato español: 1234.5 -> "1234,50 €". */
const FORMATO_EUROS = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
export const euros = (n) => FORMATO_EUROS.format(Number(n) || 0);

// Especialidades del personal. Debe coincidir con ESPECIALIDADES_PROFESIONAL
// de shared/especialidades/catalogo.js
export const ESPECIALIDADES = [
    ['medicina general', 'Medicina general y de familia'], ['anestesiologia', 'Anestesiología y reanimación'],
    ['medicina del dolor', 'Medicina del dolor'], ['aparato digestivo', 'Aparato digestivo'], ['cardiologia', 'Cardiología'],
    ['dermatologia', 'Dermatología'], ['ginecologia', 'Ginecología y obstetricia'], ['pediatria', 'Pediatría'],
    ['traumatologia', 'Traumatología y cirugía ortopédica'], ['oftalmologia', 'Oftalmología'],
    ['otorrinolaringologia', 'Otorrinolaringología'], ['urologia', 'Urología'], ['fisioterapia', 'Fisioterapia'],
    ['psicologia', 'Psicología'], ['psicologia infantil', 'Psicología infantil'], ['psicopedagogia', 'Psicopedagogía terapéutica'],
    ['logopedia', 'Logopedia'], ['odontologia', 'Odontología'], ['nutricion', 'Nutrición y dietética'],
    ['podologia', 'Podología'], ['medicina estetica', 'Medicina estética'], ['enfermeria', 'Enfermería']
];

/** Nombre legible de una especialidad: "medicina general" -> "Medicina general y de familia". */
export const nombreEspecialidad = (valor) => ESPECIALIDADES.find(e => e[0] === valor)?.[1] || valor || '';
